'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useMemo, useState } from 'react';

import { useTranslation } from '@/lib/i18n/i18n.client';

import { TopicEntry } from '@/cms/types/entry';

import Badge from '../../atoms/badge';
import { useFeedControls } from './FeedControlsContext';

import crossIconSrc from '~/svg/IconCross.svg';
import { SvgIcon } from '@/components/SvgIcon';

type Props = {
  featuredTopics: TopicEntry[];
  allTopics: TopicEntry[];
  selectedTopic?: string;
};

export const Filters = ({
  featuredTopics,
  allTopics,
  selectedTopic,
}: Props) => {
  const { texts } = useTranslation();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isPending, startTransition } = useFeedControls();

  const nonFeaturedTopics = useMemo(() => {
    if (featuredTopics.length > 0) {
      return allTopics?.filter((topic) => {
        const hasMatch = featuredTopics.some(
          (featuredTopic) => topic.topic === featuredTopic.topic
        );
        return !hasMatch;
      });
    }
    return allTopics;
  }, [allTopics, featuredTopics]);

  const [isTopicsExpanded, setIsTopicsExpanded] = useState(() => {
    if (featuredTopics.length === 0) return true;
    if (selectedTopic) {
      const isSelectedTopicHidden = nonFeaturedTopics.some(
        (nonFeaturedTopic) => nonFeaturedTopic.slug === selectedTopic
      );
      return isSelectedTopicHidden;
    }
    return false;
  });

  const shouldShowMoreButton =
    featuredTopics.length > 0 &&
    featuredTopics.length < nonFeaturedTopics.length;

  const handleFilterClick = (topicSlug: string) => {
    const params = new URLSearchParams(searchParams);

    if (topicSlug && topicSlug !== params.get('topic')) {
      params.set('topic', topicSlug);
    } else {
      params.delete('topic');
    }

    const qs = params.toString();
    startTransition(() => {
      router.replace(`/blog${qs ? `?${qs}` : ''}`, { scroll: false });
    });
  };

  return (
    <ul className='flex flex-1 flex-wrap gap-2'>
      <FilterItem
        topic={{ slug: '', topic: texts.articles_feed.all_topics }}
        isSelected={!selectedTopic}
        handleClick={handleFilterClick}
        disabled={isPending}
      />
      {featuredTopics?.map((topic) => (
        <FilterItem
          key={topic.slug}
          topic={topic}
          isSelected={selectedTopic === topic.slug}
          handleClick={handleFilterClick}
          disabled={isPending}
        />
      ))}
      {isTopicsExpanded &&
        nonFeaturedTopics?.map((topic) => (
          <FilterItem
            key={topic.slug}
            topic={topic}
            isSelected={selectedTopic === topic.slug}
            handleClick={handleFilterClick}
            disabled={isPending}
          />
        ))}
      {shouldShowMoreButton && (
        <li>
          <Badge
            variant='outlined'
            color='brand'
            classNames={{ root: 'cursor-pointer' }}
            onClick={() => setIsTopicsExpanded(!isTopicsExpanded)}
          >
            {isTopicsExpanded ? (
              <div className='h-3 w-3 rotate-180 duration-300'>
                <SvgIcon src={crossIconSrc} />
              </div>
            ) : (
              '...'
            )}
          </Badge>
        </li>
      )}
    </ul>
  );
};

const FilterItem = ({
  topic,
  handleClick,
  isSelected,
  disabled,
}: {
  topic: { slug: TopicEntry['slug']; topic: TopicEntry['topic'] };
  handleClick: (topic: string) => void;
  isSelected: boolean;
  disabled: boolean;
}) => {
  let rootCursorClass = 'cursor-pointer';
  if (disabled) {
    rootCursorClass = 'cursor-wait opacity-60';
  } else if (isSelected) {
    rootCursorClass = 'cursor-default';
  }

  return (
    <li>
      <Badge
        variant='soft'
        classNames={{
          root: rootCursorClass,
        }}
        color={isSelected ? 'brand' : 'gray'}
        onClick={() => !disabled && handleClick(topic.slug)}
      >
        {topic.topic.toUpperCase()}
      </Badge>
    </li>
  );
};
